import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useUserTickets } from '@/hooks/useTickets';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { StatusBadge, TypeBadge } from '@/components/Badges';
import { timestampToDate } from '@/types';
import { useEffect, useState } from 'react';

type SortField = 'created' | 'status' | 'priority' | 'type';

export function MyTicketsPage() {
  const { currentUser } = useAuth();
  const { tickets, loading, error } = useUserTickets(currentUser?.uid);
  const [messageCounts, setMessageCounts] = useState<Record<string, number>>({});
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    const fetchMessageCounts = async () => {
      const counts: Record<string, number> = {};
      for (const ticket of tickets) {
        const messagesQuery = query(collection(db, 'tickets', ticket.id, 'messages'));
        const messagesSnapshot = await getDocs(messagesQuery);
        counts[ticket.id] = messagesSnapshot.size;
      }
      setMessageCounts(counts);
    };
    if (tickets.length > 0) {
      fetchMessageCounts();
    }
  }, [tickets]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortedTickets = [...tickets].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'created':
        comparison = (timestampToDate(a.createdAt)?.getTime() || 0) - (timestampToDate(b.createdAt)?.getTime() || 0);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'priority':
        const priorityOrder = { LOW: 0, MEDIUM: 1, HIGH: 2 };
        comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
    }
    return sortAsc ? comparison : -comparison;
  });

  if (loading) return <ProtectedRoute><Layout><LoadingSpinner /></Layout></ProtectedRoute>;
  if (error) return (
    <ProtectedRoute><Layout>
      <div className="terminal-alert error">Error: {error.message}</div>
    </Layout></ProtectedRoute>
  );

  return (
    <ProtectedRoute>
      <Layout>
        <div className="terminal-card">
          <div className="card-header">
            <span className="card-num">01</span>
            <span className="card-title">MY TICKETS</span>
            <div className="card-header-right">
              <span className="stat-chip">TOTAL <strong>{tickets.length}</strong></span>
              <Link to="/tickets/new" className="action-btn" style={{ minWidth: 'auto', padding: '4px 12px', fontSize: '9px' }}>
                New Ticket
              </Link>
            </div>
          </div>
          <div className="card-body">
            {tickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--muted)', fontSize: '10px', letterSpacing: '0.12em' }}>
                <div style={{ marginBottom: '16px' }}>No tickets found</div>
                <Link to="/tickets/new" className="action-btn">Create Your First Ticket</Link>
              </div>
            ) : (
              <>
                {/* Sort controls */}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '9px', color: 'var(--muted)', letterSpacing: '0.12em' }}>SORT:</span>
                  {(['created', 'status', 'priority', 'type'] as SortField[]).map(field => (
                    <button
                      key={field}
                      onClick={() => handleSort(field)}
                      className={`sort-btn${sortField === field ? ' active' : ''}`}
                    >
                      {field.toUpperCase()} {sortField === field && (sortAsc ? 'â†‘' : 'â†“')}
                    </button>
                  ))}
                </div>

                {/* Tickets table */}
                <div className="table-wrap">
                  <table className="terminal-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>TITLE</th>
                        <th>STATUS</th>
                        <th>TYPE</th>
                        <th>MSGS</th>
                        <th>CREATED</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTickets.map((ticket) => (
                        <tr key={ticket.id}>
                          <td>
                            <Link to={`/tickets/${ticket.id}`} style={{ fontFamily: 'Consolas, monospace', fontSize: '9px' }}>
                              #{ticket.id.substring(0, 8).toUpperCase()}
                            </Link>
                          </td>
                          <td style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Link to={`/tickets/${ticket.id}`}>{ticket.title}</Link>
                          </td>
                          <td><StatusBadge status={ticket.status} /></td>
                          <td><TypeBadge type={ticket.type} /></td>
                          <td style={{ color: 'var(--muted)' }}>{messageCounts[ticket.id] || 0}</td>
                          <td style={{ color: 'var(--muted)', fontSize: '9px' }}>
                            {timestampToDate(ticket.createdAt) ? format(timestampToDate(ticket.createdAt)!, 'MMM d, yyyy') : 'â€”'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </Layout>
    </ProtectedRoute>
  );
}
